import { useEffect, useRef } from 'react';
import { unpositionedMissionIds } from './unpositionedMissions';

/**
 * A campaign that contains any mission without saved coordinates is an
 * incomplete layout. The on-open snap refuses to rewrite anyone until every
 * mission has x/y, so one blank mission leaves the rest of the board gappy.
 * Run the same explicit full tidy a delete uses, once per missing-id set,
 * after card sizes have settled so the pack is not the placeholder dimensions.
 */
export function useTidyWhenUnpositioned({
  tasks,
  campaignId,
  measuredElkSettled,
  useAutoPositions,
  isMultiCampaignMode,
  hasActiveFilters,
  layoutHoldRef,
  draggingNodeRef,
  handleAutoLayout,
}) {
  const tidiedSigRef = useRef('');

  useEffect(() => {
    tidiedSigRef.current = '';
  }, [campaignId]);

  useEffect(() => {
    if (isMultiCampaignMode || hasActiveFilters) return;
    if (!measuredElkSettled || useAutoPositions) return;
    if (layoutHoldRef?.current) return;
    if (draggingNodeRef?.current != null && draggingNodeRef.current !== undefined) return;
    if (!tasks?.length) return;

    const missingIds = unpositionedMissionIds(tasks);
    if (missingIds.length === 0) {
      tidiedSigRef.current = '';
      return;
    }

    const sig = missingIds.join(',');
    if (tidiedSigRef.current === sig) return;
    tidiedSigRef.current = sig;

    handleAutoLayout?.(null, { skipRecordAction: true });
  }, [
    tasks,
    campaignId,
    measuredElkSettled,
    useAutoPositions,
    isMultiCampaignMode,
    hasActiveFilters,
    layoutHoldRef,
    draggingNodeRef,
    handleAutoLayout,
  ]);
}
